import { DocumentToolsService, ToolEventSource } from './document-tools.service';
import { PdfInstanceManagerService } from '../instance/pdf-instance-manager.service';
import { Injectable } from '@angular/core';
import { TabService } from '../tab.service';
import { DocumentMarkersMode, CustomDocviewerCursorStyles, AnnotationTypes, PdfToolType, PdfTools, HighlightingTypes } from 'src/app/core/pdf-utils/enums';
import { updateCursorIcon, rgbToHex, isAnnotationType } from 'src/app/core/common';
import { IMarkerColor, IMarkerContent } from 'src/app/core/interfaces';
import { annotationColorFromHexColor } from 'src/app/core/pdf-utils/drawing-utils';
import { DocumentManagerService } from './document-manager.service';
import { AnnotationEvent } from 'src/models/Events';
import { Core, WebViewerInstance } from '@pdftron/webviewer';

const CALCULATION_ERROR = 0.01;
export const ERASER_COLOR = new (window as any).Core.Annotations.Color(220, 220, 220, 1);

@Injectable({
  providedIn: 'root'
})
export class DocumentMarkersService {
  private _currentMode: DocumentMarkersMode = DocumentMarkersMode.None;

  private get docViewer() {
    return this.tabService.activeTab && this.tabService.activeTab.id
      ? this.pdfInstanceManager.getDocViewer(this.tabService.activeTab.id)
      : null;
  }

  private get viewerInstance(): WebViewerInstance | null {
    return this.tabService.activeTab && this.tabService.activeTab.id
      ? this.pdfInstanceManager.getViewerInstance(this.tabService.activeTab.id)
      : null;
  }

  private get documentId(): string | null {
    return this.tabService.activeTab ? this.tabService.activeTab.id : null;
  }

  private get annotationManager(): Core.AnnotationManager | null {
    return this.docViewer ? this.docViewer.getAnnotationManager() : null;
  }

  get currentMode(): DocumentMarkersMode { return this._currentMode; }

  constructor(
    private readonly pdfInstanceManager: PdfInstanceManagerService,
    private readonly docToolsService: DocumentToolsService,
    private readonly tabService: TabService,
    private readonly docMgrService: DocumentManagerService,
  ) {
    this.pdfInstanceManager.annotationSelected.subscribe(
      (e: AnnotationEvent<Core.Annotations.Annotation>) => {
        if (e && e.annotations && e.annotations.length) {
          const highlightAnnots = e.annotations.filter(x => x.ToolName === PdfTools.AnnotationCreateTextHighlight)

          if (highlightAnnots.length > 0) {
            this.annotationManager?.deselectAnnotations(highlightAnnots); // to prevent draw selection outline and menu
          }
        }
      }
    );
  }

  async addMarkerHandler(annotation: Core.Annotations.TextHighlightAnnotation) {
    if (!annotation || (annotation && annotation.Author && (annotation.Author === HighlightingTypes.Word || annotation.Author === HighlightingTypes.Sentence || annotation.Author === HighlightingTypes.WordBeforeTextNote))) {
      return;
    }
    this.onAnnotationAdded(annotation);
  }

  setMarkerMode(mode: DocumentMarkersMode) {
    this.setHighlightingMode(mode);
  }

  setMarkerColor(color: IMarkerColor) {
    this.setHighlightColor(color);
  }

  async getMarkerAnnotations(): Promise<IMarkerContent[]> {
    return (await this.getDocumentMarkers(true)).map(m => {
      const { R, G, B } = m.StrokeColor;

      // before m.getContents() after m.getCustomData('trn-annot-preview').
      // GetContents return empty string. Please see previos commits for details.
      // https://github.com/Sensotec/Asgard/commit/f202190ed84c238f574b47b8190111e17915dfa4
      // https://sensotec.atlassian.net/browse/ASG-2244
      let text = m.getCustomData('trn-annot-preview');

      // in the older versions of pdftron, there is no 'trn-annot-preview'
      // so if there isn't, get the text in the annotation square
      if (!text) {
        text = this.getAnnotationText(m);
      }

      return {
        text,
        color: rgbToHex(R, G, B)
      } as IMarkerContent;
    });
  }

  setHighlightingMode(mode: DocumentMarkersMode) {
    this._currentMode = mode;

    if (mode === DocumentMarkersMode.None) {
      this.docToolsService.disableTool(ToolEventSource.Markers);
      return;
    } else if (mode === DocumentMarkersMode.Eraser) {
      if (this.documentId) {
        this.docToolsService.setToolStyles(this.documentId, PdfToolType.Marker, { StrokeColor: ERASER_COLOR }, true);
      }
      if (this.documentId) {
        updateCursorIcon(this.documentId, CustomDocviewerCursorStyles.erase);
      }
    }

    this.docToolsService.enableTool(ToolEventSource.Markers, PdfToolType.Marker);
  }

  setHighlightColor(color: IMarkerColor) {
    if (this.documentId) {
      this.docToolsService.setToolStyles(
        this.documentId,
        PdfToolType.Marker, { StrokeColor: annotationColorFromHexColor(color.hex) }, true
      );
    }
    if (this.documentId) {
      updateCursorIcon(this.documentId, CustomDocviewerCursorStyles[color.color]);
    }
  }


  async getDocumentMarkers(sortMarkers = false): Promise<Core.Annotations.TextHighlightAnnotation[]> {
    if (!this.docMgrService.documentReady || !this.annotationManager) {
      return [];
    }
    const markers = (await this.annotationManager
      .getAnnotationsList())
      .filter(
        a => isAnnotationType(a, [AnnotationTypes.Marker]) // instanceof Annotations.TextHighlightAnnotation
      ) as Core.Annotations.TextHighlightAnnotation[];

    return !sortMarkers ? markers : markers.sort((marker1, marker2) => {
      if (marker1.PageNumber !== marker2.PageNumber) {
        return marker1.PageNumber - marker2.PageNumber;
      }
      if (!this.areCoordsEqual(marker1.Quads[0].y1, marker2.Quads[0].y1)) {
        return marker1.Quads[0].y1 - marker2.Quads[0].y1;
      }
      if (!this.areCoordsEqual(marker1.Quads[0].x1, marker2.Quads[0].x1)) {
        return marker1.Quads[0].x1 - marker2.Quads[0].x1;
      }
      return 0;
    });
  }

  private async onAnnotationAdded(annotation: Core.Annotations.TextHighlightAnnotation) {
    const markersAnnotations = await this.getDocumentMarkers();

    for (const marker of markersAnnotations) {
      if (
        marker.Id === annotation.Id ||
        marker.PageNumber !== annotation.PageNumber
      ) {
        if (this.isEraserColor(annotation.StrokeColor)) {
          await this.deleteMarkers([annotation]);
        }
        continue;
      }

      const isStartInside = this.isAnnotationStartInsideAnnotation(
        marker,
        annotation
      );
      const isEndInside = this.isAnnotationEndInsideAnnotation(
        marker,
        annotation
      );

      if (isStartInside && isEndInside) {
        await this.deleteMarkers([marker]);
      }

      if (isStartInside && !isEndInside) {
        const annotationLastQuad =
          annotation.Quads[annotation.Quads.length - 1];
        const quadIndex = this.findCorrespondingStartQuadIndex(
          annotationLastQuad,
          marker
        );
        if (quadIndex === -1) {
          return;
        }
        const markerStartQuad = marker.Quads[quadIndex];

        // if the or width of the start quad ends up to be zero, remove that quad
        if (markerStartQuad.x2 === annotationLastQuad.x2 && markerStartQuad.x3 === annotationLastQuad.x3) {
          marker.Quads.splice(0, quadIndex + 1);
        } else {
          markerStartQuad.x1 = annotationLastQuad.x2;
          markerStartQuad.y1 = annotationLastQuad.y2;
          markerStartQuad.x4 = annotationLastQuad.x3;
          markerStartQuad.y4 = annotationLastQuad.y3;
          marker.Quads.splice(0, quadIndex);
        }

        await this.setAnnotationContent(marker);
        if (this.annotationManager) {
          this.annotationManager.redrawAnnotation(marker);
        }
      }

      if (!isStartInside && isEndInside) {
        const annotationFirstQuad = annotation.Quads[0];
        const quadIndex = this.findCorrespondingEndQuadIndex(
          annotationFirstQuad,
          marker
        );
        if (quadIndex === -1) {
          return;
        }
        const markerEndQuad = marker.Quads[quadIndex];

        // if the or width of the end quad ends up to be zero, remove that quad
        if (markerEndQuad.x4 === annotationFirstQuad.x4 && markerEndQuad.x1 === annotationFirstQuad.x1) {
          marker.Quads.splice(quadIndex);
        } else {
          markerEndQuad.x2 = annotationFirstQuad.x1;
          markerEndQuad.y2 = annotationFirstQuad.y1;
          markerEndQuad.x3 = annotationFirstQuad.x4;
          markerEndQuad.y3 = annotationFirstQuad.y4;
          marker.Quads.splice(quadIndex + 1);
        }
        this.setAnnotationContent(marker);
        if (this.annotationManager) {
          this.annotationManager.redrawAnnotation(marker);
        }
      }

      if (!isStartInside && !isEndInside) {
        if (this.isAnnotationStartInsideAnnotation(annotation, marker)) {
          await this.splitMarker(marker, annotation);
        }
      }
    }

    if (this.isEraserColor(annotation.StrokeColor)) {
      await this.deleteMarkers([annotation]);
    }

    // this.clearSelection();
  }

  private isEraserColor(color: Core.Annotations.Color): boolean {
    const { R, G, B } = color;
    return rgbToHex(R, G, B) === '#dcdcdc';
  }

  private isCoordinateBigger(c1: number, c2: number) {
    return c1 >= c2 || this.areCoordsEqual(c1, c2);
  }

  private areCoordsEqual(c1: number, c2: number) {
    return Math.abs(c1 - c2) <= CALCULATION_ERROR;
  }

  private isPointInQuad(pt: Core.Math.Point, quad: Core.Math.Quad) {
    return (
      this.isCoordinateBigger(pt.x, quad.x1) &&
      this.isCoordinateBigger(quad.x2, pt.x) &&
      this.isCoordinateBigger(quad.y1, pt.y) &&
      this.isCoordinateBigger(pt.y, quad.y4)
    );
  }

  private isQuadInsideQuad(qd1: Core.Math.Quad, qd2: Core.Math.Quad) {
    const viewerInstance = this.viewerInstance;

    if (viewerInstance) {
      return (
        this.isPointInQuad(new viewerInstance.Core.Math.Point(qd1.x1, qd1.y1), qd2) &&
        this.isPointInQuad(new viewerInstance.Core.Math.Point(qd1.x2, qd1.y2), qd2) &&
        this.isPointInQuad(new viewerInstance.Core.Math.Point(qd1.x3, qd1.y3), qd2) &&
        this.isPointInQuad(new viewerInstance.Core.Math.Point(qd1.x4, qd1.y4), qd2)
      );
    }
  }

  private findParentQuadIndex(
    childQuad: Core.Math.Quad,
    parentAnnotationQuads: Core.Math.Quad[]
  ): number {
    return parentAnnotationQuads.findIndex(q =>
      this.isQuadInsideQuad(childQuad, q)
    );
  }

  private findCorrespondingStartQuadIndex(
    annotationLastQuad: Core.Math.Quad,
    marker: Core.Annotations.TextMarkupAnnotation
  ): number {
    return this.findCorrespondingQuadIndex(annotationLastQuad, marker.Quads, true);
  }

  private findCorrespondingEndQuadIndex(
    annotationFirstQuad: Core.Math.Quad,
    marker: Core.Annotations.TextMarkupAnnotation
  ): number {
    return this.findCorrespondingQuadIndex(annotationFirstQuad, marker.Quads, false);
  }

  private findCorrespondingQuadIndex(
    annotationQuad: Core.Math.Quad,
    markerQuads: Core.Math.Quad[],
    isStart: boolean
  ): number {
    const viewerInstance = this.viewerInstance;

    if (viewerInstance === null) {
      return -1;
    }

    return markerQuads.findIndex(qd => {
      const boundaryPoints = isStart
        ? [
          new viewerInstance.Core.Math.Point(qd.x1, qd.y1),
          new viewerInstance.Core.Math.Point(qd.x4, qd.y4)
        ]
        : [
          new viewerInstance.Core.Math.Point(qd.x2, qd.y2),
          new viewerInstance.Core.Math.Point(qd.x3, qd.y3)
        ];

      return boundaryPoints.every(pt => this.isPointInQuad(pt, annotationQuad));
    });
  }

  private isAnnotationStartInsideAnnotation(
    ann1: Core.Annotations.TextMarkupAnnotation,
    ann2: Core.Annotations.TextMarkupAnnotation
  ): boolean {
    const viewerInstance = this.viewerInstance;

    if (viewerInstance === null) {
      return false;
    }

    const startPoint = new viewerInstance.Core.Math.Point(
      ann1.Quads[0].x1,
      ann1.Quads[0].y1
    );
    return !!ann2.Quads.find(quad => this.isPointInQuad(startPoint, quad));
  }

  private isAnnotationEndInsideAnnotation(
    ann1: Core.Annotations.TextMarkupAnnotation,
    ann2: Core.Annotations.TextMarkupAnnotation
  ) {
    const viewerInstance = this.viewerInstance;

    if (viewerInstance === null) {
      return false;
    }

    const lastQuad = ann1.Quads[ann1.Quads.length - 1];
    const endPoint = new viewerInstance.Core.Math.Point(lastQuad.x2, lastQuad.y2);
    return !!ann2.Quads.find(quad => this.isPointInQuad(endPoint, quad));
  }


  private async deleteMarkers(markers: Core.Annotations.TextHighlightAnnotation[]) {
    if (this.annotationManager) {
      await this.annotationManager.deleteAnnotations(markers, { imported: false, force: true });
    }
  }

  private async splitMarker(marker: Core.Annotations.TextHighlightAnnotation, annotation: Core.Annotations.TextHighlightAnnotation) {
    const annotationFirstQuad = annotation.Quads[0];
    const annotationLastQuad =
      annotation.Quads[annotation.Quads.length - 1];
    const startQuadIndex = this.findParentQuadIndex(
      annotationFirstQuad,
      marker.Quads
    );
    const endQuadIndex = this.findParentQuadIndex(
      annotationLastQuad,
      marker.Quads
    );
    if (this.annotationManager) {
      const ann1 = await this.annotationManager.getAnnotationCopy(
        marker, {copyAssociatedLink: true}
      ) as Core.Annotations.TextHighlightAnnotation;
      const ann2 = await this.annotationManager.getAnnotationCopy(
        marker, {copyAssociatedLink: true}
      ) as Core.Annotations.TextHighlightAnnotation;

      ann1.Quads = ann1.Quads.slice(0, startQuadIndex + 1);
      const ann1BorderQuad = ann1.Quads[startQuadIndex];
      ann1BorderQuad.x2 = annotationFirstQuad.x1;
      ann1BorderQuad.y2 = annotationFirstQuad.y1;
      ann1BorderQuad.x3 = annotationFirstQuad.x4;
      ann1BorderQuad.y3 = annotationFirstQuad.y4;


      ann2.Quads = ann2.Quads.slice(endQuadIndex);
      const ann2BorderQuad = ann2.Quads[0];
      ann2BorderQuad.x1 = annotationLastQuad.x2;
      ann2BorderQuad.y1 = annotationLastQuad.y2;
      ann2BorderQuad.x4 = annotationLastQuad.x3;
      ann2BorderQuad.y4 = annotationLastQuad.y3;

      // remove quads that form a line and not a box
      // 4 x----x 3
      //   |    |
      // 1 x----x 2
      ann1.Quads = ann1.Quads.filter(quad => quad.x1 !== quad.x2 && quad.x4 !== quad.x3 && quad.y4 !== quad.y1 && quad.y3 !== quad.y2);
      ann2.Quads = ann2.Quads.filter(quad => quad.x1 !== quad.x2 && quad.x4 !== quad.x3 && quad.y4 !== quad.y1 && quad.y3 !== quad.y2);

      await this.deleteMarkers([marker]);
      await this.setAnnotationContent(ann1);
      await this.setAnnotationContent(ann2);
      if (this.annotationManager) {
        this.annotationManager.addAnnotations([ann1, ann2]);
        this.annotationManager.redrawAnnotation(ann1);
        this.annotationManager.redrawAnnotation(ann2);
      }
    }
  }

  private async setAnnotationContent(ann: Core.Annotations.TextHighlightAnnotation): Promise<void> {
    const startPoint = {
      x: ann.Quads[0].x4,
      y: ann.Quads[0].y4,
      pageNumber: ann.PageNumber
    };

    const endPoint = {
      x: ann.Quads[ann.Quads.length - 1].x2,
      y: ann.Quads[ann.Quads.length - 1].y2,
      pageNumber: ann.PageNumber
    };

    if (this.docViewer) {
      this.docViewer.select(startPoint, endPoint);
      const result = this.docViewer.getSelectedText(ann.PageNumber);
      this.docViewer.clearSelection();
      ann.setContents(result);
      // see getMarkerAnnotations()
      ann.setCustomData('trn-annot-preview', result);
    }
  }

  private getAnnotationText(ann: Core.Annotations.TextHighlightAnnotation): string {
    const startPoint = {
      x: ann.Quads[0].x4,
      y: ann.Quads[0].y4,
      pageNumber: ann.PageNumber
    };

    const endPoint = {
      x: ann.Quads[ann.Quads.length - 1].x2,
      y: ann.Quads[ann.Quads.length - 1].y2,
      pageNumber: ann.PageNumber
    };

    if (this.docViewer) {
      this.docViewer.select(startPoint, endPoint);
      return this.docViewer.getSelectedText(ann.PageNumber);
    }

    return '';
  }

  // private clearSelection() {
  //   if (this.docViewer) {
  //     this.docViewer.clearSelection();
  //   }
  // }
}
